import React, {useEffect, useState} from 'react';

import {QueryResultType} from 'graphql-client/BenchlingQuery';
import ColumnSelector from 'notebook/sample-propagation/add-samples-modal/ColumnSelector';
import SourceSampleSelectionTable from 'notebook/sample-propagation/sample-selection/SourceSampleSelectionTable';
import getSampleDescriptor from 'notebook/sample-propagation/util/getSampleDescriptor';
import getSampleDetailsName from 'notebook/sample-propagation/util/getSampleDetailsName';
import {worklistTypeToSampleFilter} from 'notebook/sample-propagation/util/SampleTypeWorklistTypeConversions';
import {
  StructuredTableAddOperationOptions,
  StructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {isTableLengthLocked} from 'notebook/structured-tables/util/isTableLengthLocked';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import {WorklistType} from 'worklists/WorklistConstants';

import {NO_DESTINATIONS_AVAILABLE, NO_SAMPLES_AVAILABLE} from './add-samples-modal/AddSamplesModalConstants';
import nonExpansionFooterMessage from './add-samples-modal/nonExpansionFooterMessage';
import {SampleWithRowCount} from './AddSamplesFromWorklistModal';
import {SampleFilter, sampleFilterToIcon} from './SampleFilter';
import useHydrateWorklistSamples from './useHydrateWorklistSamples';
import {maximumNumberOfSamplesThatCanBeAddedBeforeExpansion} from './util/addSamplesToTable';
import getSampleId from './util/getSampleId';
import makeSampleDetailsFromSampleIds from './util/makeSampleDetailsFromSampleIds';

interface AddSamplesFromBarcodeScannerModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (addSamplesCommand: {
    itemIds: Array<string>;
    destinationColumnsCids: Array<string> | null;
  }) => void;
  scannedItemType: WorklistType;
  scannedItemIds: Array<string>;
  destinationSchemaId?: string | null;
  // When the destination is another structured table, you must provide a list of
  // {sample filter, destination column array, name displayed in modal} objects.
  destinationSampleFiltersByColumn?: Array<{
    sampleFilters: Array<SampleFilter>;
    columns: Array<StructuredTableColumn>;
    columnIndices?: Array<number>;
    destinationDisplayName: string;
  }>;
  // Otherwise, you must provide a sampleFilter, indicating what you will accept
  destinationSampleType?: SampleFilter | null;
  destinationTable?: UnknownStructuredTable;
  options?: StructuredTableAddOperationOptions;
}

interface ScannedItemsStatusMessageProps {
  numberOfUsableSamples: number;
  numberOfUnusableSamples: number;
  destinationColumnName: string | null;
  unusableSampleIds: Array<string>;
  scannedItemType: WorklistType;
}

function ScannedItemsStatusMessage({
  numberOfUsableSamples,
  numberOfUnusableSamples,
  destinationColumnName,
  unusableSampleIds,
  scannedItemType,
}: ScannedItemsStatusMessageProps): JSX.Element | null {
  let errorMessage: string | null = null;
  const unusableSamples = makeSampleDetailsFromSampleIds(unusableSampleIds);
  const unusableSamplesBarcodes = unusableSamples.map((sampleDetails) =>
    getSampleDetailsName(sampleDetails, true)
  );
  const sampleDescriptor = getSampleDescriptor(worklistTypeToSampleFilter(scannedItemType));

  if (numberOfUsableSamples === 0) {
    if (numberOfUnusableSamples === 0) {
      errorMessage = 'No samples were scanned.';
    } else {
      errorMessage = `None of the ${numberOfUnusableSamples} scanned ${sampleDescriptor.plural} are
       available and compatible with the ${
         destinationColumnName ? `${destinationColumnName} column` : 'selected destination'
       }: ${unusableSamplesBarcodes.join(', ')}`;
    }
  } else if (numberOfUnusableSamples > 0) {
    errorMessage = `${numberOfUnusableSamples} ${
      numberOfUnusableSamples > 1 ? `${sampleDescriptor.plural} were` : `${sampleDescriptor.singular} was`
    } hidden because ${numberOfUnusableSamples > 1 ? 'they are ' : 'it is '}
    unavailable${
      destinationColumnName ? ` or incompatible with the ${destinationColumnName} column` : ''
    }: ${unusableSamplesBarcodes.join(', ')}`;
  }

  return errorMessage ? (
    <div className="u-small-spacing-bottom">
      <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
    </div>
  ) : null;
}

export default function AddSamplesFromBarcodeScannerModal(
  props: AddSamplesFromBarcodeScannerModalProps
): JSX.Element {
  const {destinationSampleFiltersByColumn, destinationSchemaId} = props;
  const [validIds, setValidIds] = useState<Array<string>>(props.scannedItemIds);
  const [unusableIds, setUnusableIds] = useState<Array<string>>([]);
  const [samplesWithSchemaMismatch, setSamplesWithSchemaMismatch] = useState<Array<string>>([]);
  const [destinationColumnIndex, setDestinationColumnIndex] = useState<number | null>(
    destinationSampleFiltersByColumn?.length === 1 ? 0 : null
  );

  const [samplesWithRowCounts, setSamplesWithRowCounts] = useState<Array<SampleWithRowCount> | null>(null);
  const selectedDestination =
    destinationColumnIndex == null ? undefined : destinationSampleFiltersByColumn?.[destinationColumnIndex];
  const primarySampleFilter =
    selectedDestination?.sampleFilters.length === 1 ? selectedDestination.sampleFilters[0] : null;

  const descriptor = getSampleDescriptor(
    props.destinationSampleType ?? (destinationColumnIndex != null ? primarySampleFilter : null)
  );
  // The SourceSampleSelectionTable takes an array of full SampleDetails objects. However, the barcode scanner
  // only stores the IDs of items scanned. We query the server to ensure that the sample _names_ are in the store,
  // so we can display them to the user in the table.
  const {status: hydrationState, schemaIdBySampleId} = useHydrateWorklistSamples(
    // We attempt to hydrate all scanned samples; not filtered in any way
    props.scannedItemIds
  );
  const preventTableExpansion =
    (isTableLengthLocked(props.destinationTable) || props.options?.preventTableExpansion) ?? false;
  const preventTableExpansionMessage = props.options?.preventTableExpansionMessage;

  const itemIdsToSubmit: Array<string> = [];
  if (samplesWithRowCounts != null) {
    for (const {sample, rowCount} of samplesWithRowCounts) {
      const rowSampleIds = Array(rowCount).fill(getSampleId(sample));
      itemIdsToSubmit.push(...rowSampleIds);
    }
  }

  // Update the list of samples that don't match the destination schema. Samples without a schema
  // are allowed through, and if no schema is present on the destination, all samples pass through
  useEffect(() => {
    setSamplesWithSchemaMismatch(
      destinationSchemaId == null
        ? []
        : props.scannedItemIds.filter((itemId) => {
            const sampleSchemaId = schemaIdBySampleId[itemId];
            return sampleSchemaId != null && sampleSchemaId !== destinationSchemaId;
          })
    );
  }, [props.scannedItemIds, destinationSchemaId, schemaIdBySampleId]);

  // Reassess samples for validity
  useEffect(() => {
    // Items must have a matching (or null) schema if destinationSchemaId specified
    // Archived and inaccessible will be removed downstream
    const newValidIds = props.scannedItemIds.filter((itemId) => !samplesWithSchemaMismatch.includes(itemId));

    setValidIds(newValidIds);
    setUnusableIds(props.scannedItemIds.filter((itemId) => !newValidIds.includes(itemId)));
  }, [props.scannedItemIds, samplesWithSchemaMismatch]);

  function handleSubmit(): void {
    props.onRequestSubmit({
      itemIds: itemIdsToSubmit,
      destinationColumnsCids:
        destinationSampleFiltersByColumn && destinationColumnIndex != null
          ? destinationSampleFiltersByColumn[destinationColumnIndex].columns.map((col) => col.cid)
          : null,
    });
  }

  const columnIndices = selectedDestination?.columnIndices;
  const nonExpansionModalFooter =
    preventTableExpansion &&
    destinationSampleFiltersByColumn &&
    props.destinationTable &&
    columnIndices != null &&
    destinationColumnIndex != null
      ? nonExpansionFooterMessage({
          numberOfSelectedSamples: itemIdsToSubmit.length,
          maximumNumberOfSamplesBeforeExpansion: maximumNumberOfSamplesThatCanBeAddedBeforeExpansion(
            props.destinationTable,
            columnIndices
          ),
          preventTableExpansionMessage:
            preventTableExpansionMessage || 'This table cannot expand with new rows.',
          sampleDescriptor: descriptor,
        })
      : null;

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      title={`Add ${descriptor.plural} from scanned barcodes`}
      onRequestSubmit={handleSubmit}
      buttons={[
        {
          submit: true,
          text: `Add ${descriptor.plural}${itemIdsToSubmit.length > 0 ? ` (${itemIdsToSubmit.length})` : ''}`,
          disabled: itemIdsToSubmit.length === 0 || nonExpansionModalFooter != null,
          style: ButtonStyle.SECONDARY,
        },
      ]}
      footer={nonExpansionModalFooter}
    >
      {destinationSampleFiltersByColumn?.length === 0 ? (
        <p>{NO_DESTINATIONS_AVAILABLE}</p>
      ) : props.scannedItemIds.length === 0 ? (
        <p>{NO_SAMPLES_AVAILABLE}</p>
      ) : (
        <>
          <div className="u-small-spacing-bottom">
            {destinationSampleFiltersByColumn && (
              <ColumnSelector
                label="Destination column"
                columns={destinationSampleFiltersByColumn.map((destinationInfo) => ({
                  name: destinationInfo.destinationDisplayName,
                  icon: sampleFilterToIcon(destinationInfo.sampleFilters[0]),
                }))}
                onChange={setDestinationColumnIndex}
                selectedColumnIndex={destinationColumnIndex}
              />
            )}
          </div>
          {props.scannedItemIds && (
            <ScannedItemsStatusMessage
              numberOfUsableSamples={validIds.length}
              numberOfUnusableSamples={unusableIds.length}
              destinationColumnName={selectedDestination?.destinationDisplayName ?? null}
              unusableSampleIds={unusableIds}
              scannedItemType={props.scannedItemType}
            />
          )}

          {hydrationState === QueryResultType.SUCCESS ? (
            <SourceSampleSelectionTable
              sourceKey="scanner"
              samples={makeSampleDetailsFromSampleIds(validIds)}
              sampleDescriptor={getSampleDescriptor(worklistTypeToSampleFilter(props.scannedItemType))}
              onChangeSelection={(newSamplesWithRowCounts) => {
                setSamplesWithRowCounts(newSamplesWithRowCounts);
              }}
            />
          ) : (
            <CenteredHorizontalThrobber />
          )}
        </>
      )}
    </Modal>
  );
}
